// src/App.js

import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Layout from './components/Layout';
import HomePage from './pages/HomePage';
import LandingPage from './pages/LandingPage';
import T178AccessSelector from './pages/T178AccessSelector';
import PermissionRequest from './pages/PermissionRequest';
import RulesetRequest from './pages/RulesetRequest';
import BindleRequest from './pages/BindleRequest';
import ConsultationRequest from './pages/ConsultationRequest';
import UATRequest from './pages/UATRequest';
import DataRequest from './pages/DataRequest';
import AccessRemoval from './pages/AccessRemoval';
import TroubleshootingRequest from './pages/TroubleshootingRequest';
import T321Request from './pages/T321Request';
import T321AccessSelector from './pages/T321AccessSelector';
import T321RulesetRequest from './pages/T321RulesetRequest';
import UFSValidationResults from './pages/UFSValidationResults';
import ViewerPage from './pages/ViewerPage';
import { getCurrentUser, onSessionExpiryWarning, initAuthAutoRefresh } from './services/auth';
import './App.css';

function App() {
    const [userAlias, setUserAlias] = useState(null);
    const [isAuthLoading, setIsAuthLoading] = useState(true);
    const [sessionWarning, setSessionWarning] = useState('');
    const authCalled = useRef(false);

    useEffect(() => {
        if (authCalled.current) return;
        authCalled.current = true;

        // Register session warning callback
        onSessionExpiryWarning((message) => {
            setSessionWarning(message);
            // Auto-dismiss after 10 seconds
            setTimeout(() => setSessionWarning(''), 10000);
        });

        getCurrentUser()
            .then(alias => {
                if (alias) {
                    setUserAlias(alias);
                    localStorage.setItem('ufs_user_alias', alias.toLowerCase());
                    // Proactive silent-refresh (visibility/focus/interval) so a token never
                    // silently expires on an idle tab — same fix proven on the approver app.
                    initAuthAutoRefresh();
                }
                setIsAuthLoading(false);
            })
            .catch(err => {
                console.error('Auth error:', err);
                setIsAuthLoading(false);
            });
    }, []);

    if (isAuthLoading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', flexDirection: 'column', gap: '12px' }}>
                <div className="spinner"></div>
                <p style={{ color: '#666', fontSize: '14px' }}>Authenticating...</p>
            </div>
        );
    }

    return (
        <Router>
            <div className="App">
                {/* Session warning toast */}
                {sessionWarning && (
                    <div style={{
                        position: 'fixed', top: '12px', right: '12px', zIndex: 9999,
                        padding: '12px 20px', borderRadius: '8px',
                        background: '#fffbeb', border: '1px solid #fde68a',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        fontSize: '13px', color: '#92400e', fontWeight: '500',
                        display: 'flex', alignItems: 'center', gap: '8px'
                    }}>
                        <span>⏳</span> {sessionWarning}
                        <button onClick={() => setSessionWarning('')}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#92400e', fontWeight: '700', fontSize: '16px', marginLeft: '8px' }}>×</button>
                    </div>
                )}
                <Routes>
                    <Route path="/" element={<HomePage userAlias={userAlias} />} />
                    <Route path="/tenants" element={<LandingPage userAlias={userAlias} />} />
                    <Route path="/consultation" element={<Layout><ConsultationRequest userAlias={userAlias} /></Layout>} />
                    <Route path="/uat" element={<Layout><UATRequest userAlias={userAlias} /></Layout>} />
                    <Route path="/data-request" element={<Layout><DataRequest userAlias={userAlias} /></Layout>} />
                    <Route path="/access-removal" element={<Layout><AccessRemoval userAlias={userAlias} /></Layout>} />
                    <Route path="/troubleshooting" element={<Layout><TroubleshootingRequest userAlias={userAlias} /></Layout>} />
                    <Route path="/request" element={<Layout><T178AccessSelector userAlias={userAlias} /></Layout>} />
                    <Route path="/request/individual" element={<Layout><PermissionRequest userAlias={userAlias} /></Layout>} />
                    <Route path="/request/ruleset" element={<Layout><RulesetRequest userAlias={userAlias} /></Layout>} />
                    <Route path="/request/bindle" element={<Layout><BindleRequest userAlias={userAlias} /></Layout>} />
                    {String(process.env.REACT_APP_ENABLE_T321).toLowerCase() === 'true' && (
                        // T321 Ruleset (flag on): /t321 shows the 2-card selector; the existing
                        // Individual flow moves to /t321/individual. Flag off: /t321 → T321Request as before.
                        String(process.env.REACT_APP_ENABLE_T321_RULESET).toLowerCase() === 'true' ? (
                            <>
                                <Route path="/t321" element={<Layout><T321AccessSelector userAlias={userAlias} /></Layout>} />
                                <Route path="/t321/individual" element={<Layout><T321Request userAlias={userAlias} /></Layout>} />
                                <Route path="/t321/ruleset" element={<Layout><T321RulesetRequest userAlias={userAlias} /></Layout>} />
                            </>
                        ) : (
                            <Route path="/t321" element={<Layout><T321Request userAlias={userAlias} /></Layout>} />
                        )
                    )}
                    <Route path="/my-requests" element={<Layout><UFSValidationResults userAlias={userAlias} /></Layout>} />
                    <Route path="/viewer" element={<ViewerPage userAlias={userAlias} />} />
                </Routes>
            </div>
        </Router>
    );
}

export default App;